import React from 'react';
import { Button } from 'antd';
import { LoadingOutlined, RightOutlined } from '@ant-design/icons';
import { JOB_FIT_ALIGNMENT_CLASS, formatAlignmentLabel } from './jobFit.shared';
import JobFitDisclaimer from './JobFitDisclaimer';
import type { JobFitFlow } from './useJobFitFlow';
import ToolRunAllowance from '../../../support/ToolRunAllowance';

type JobFitTeaserCardProps = {
  jobTitle: string;
  flow: JobFitFlow;
};

const JobFitTeaserCard: React.FC<JobFitTeaserCardProps> = ({
  jobTitle,
  flow,
}) => {
  const { phase, fitResult, gateStatus, openModal } = flow;

  return (
    <section
      className="public-panel job-fit-card job-fit-teaser"
      aria-labelledby="job-fit-heading"
    >
      <div className="public-panel-body ats-stack">
        <div>
          <p className="public-eyebrow mb-1">Resume fit</p>
          <h2
            id="job-fit-heading"
            className="jo-heading m-0 text-lg font-black"
          >
            Check your fit
          </h2>
          <p className="jo-body-subtle mb-0 mt-2 text-sm">
            See how your resume aligns with{' '}
            <span className="font-semibold text-[var(--jo-text)]">
              {jobTitle}
            </span>
            .
          </p>
        </div>

        <ToolRunAllowance status={gateStatus} />

        {phase === 'idle' && (
          <Button
            type="primary"
            className="public-button-primary w-full job-fit-teaser-cta"
            onClick={openModal}
          >
            Check your fit
            <RightOutlined />
          </Button>
        )}

        {phase === 'working' && (
          <div className="job-fit-teaser-status" aria-live="polite">
            <LoadingOutlined spin className="job-fit-teaser-status__icon" />
            <span className="job-fit-teaser-status__text">
              Analyzing your resume…
            </span>
            <Button
              type="link"
              className="job-fit-teaser-status__link"
              onClick={openModal}
            >
              View progress
            </Button>
          </div>
        )}

        {phase === 'completed' && fitResult && (
          <div className="job-fit-teaser-result ats-stack">
            <div
              className={`job-fit-score job-fit-score--compact ${JOB_FIT_ALIGNMENT_CLASS[fitResult.alignment]}`}
              aria-label={`Fit score ${fitResult.score} out of 100`}
            >
              <span className="job-fit-score__value">{fitResult.score}</span>
              <span className="job-fit-score__label">
                {formatAlignmentLabel(fitResult.alignment)}
              </span>
            </div>
            <Button
              type="default"
              className="public-button-secondary w-full"
              onClick={openModal}
            >
              View results
            </Button>
          </div>
        )}

        {phase === 'failed' && (
          <div className="job-fit-teaser-result ats-stack">
            <p className="job-fit-error mb-0" role="alert">
              We could not complete the fit check.
            </p>
            <Button
              type="default"
              className="public-button-secondary w-full"
              onClick={openModal}
            >
              Try again
            </Button>
          </div>
        )}

        {phase === 'payment' && (
          <div className="job-fit-teaser-result ats-stack">
            <p className="jo-body-subtle mb-0 text-sm">
              You&apos;ve used your free checks. A supporter code unlocks more.
            </p>
            <Button
              type="primary"
              className="public-button-primary w-full"
              onClick={openModal}
            >
              Unlock more checks
            </Button>
          </div>
        )}

        <JobFitDisclaimer />
      </div>
    </section>
  );
};

export default JobFitTeaserCard;
